import { DatePipe } from '@angular/common';
import { Component, OnInit, Input } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { EventType } from '@angular/router';
import {
  ProgramTypesGUID,
  EventWorkshop,
  ProgramTypes
} from '../../../../../models/_enum-collections';
import { Confidence } from '../../../../../models/confidence.model';
import { EventTargetProduct } from '../../../../../models/event-target-product.model';
import { MonthList } from '../../../../../models/month-list.model';
import { OsmosisEditService } from '../../../../services/osmosis-edit.service';
import { PartnerWorkshopApi } from '../../../../services/partner-workshop.service';
import { UpdateEventPaymentInformationRequest } from '../../../../../models/event-payment-information.model';
import { CommonSharedModule } from '../../../../common/shared.module';
import { EventDashboardService } from '../../../../services/event-dashboard.service';
import { EventTypeService } from '../../../../services/event-type.service';

@Component({
  selector: 'app-event-payment-information',
  templateUrl: './event-payment-information.component.html',
  styleUrls: ['./event-payment-information.component.scss'],
  standalone: true,
  imports: [CommonSharedModule]
})
export class EventPaymentInformationComponent implements OnInit {
  isReadonlyPOE = true;
  @Input() eventId!: number;
  @Input() isMRPPProgram!: boolean;
  @Input() programTypeGUID!: string;
  msgsPoe!: any[];
  btnNavPoe = false;

  maxYearDate!: Date;
  monthList!: MonthList[];
  confidence!: Confidence[];
  eventTypes!: EventType[];
  eventTargetProducts!: EventTargetProduct[];
  eventPoeInformationForm!: FormGroup;
  buttonClicked = false;
  isDealAssociation!: boolean;
  isMultiTargetProductSupported = false;

  constructor(
    private formBuilder: FormBuilder,
    private osmEdit: OsmosisEditService,
    private partnerWorkshopService: PartnerWorkshopApi,
    private eventDashboardService: EventDashboardService,
    private eventTypeService: EventTypeService
  ) {}

  ngOnInit() {
    this.loadForm();
    this.getExpectedPaymentMonth();
    this.getConfidence();
    this.getEventTypes();
    this.getTargetProducts();
    this.loadEventPaymentInformation();
  }

  loadForm() {
    this.maxYearDate = new Date(
      new Date().setFullYear(new Date().getFullYear())
    );
    this.eventPoeInformationForm = this.formBuilder.group({
      scheduledEventDate: [''],
      expectedPOEDate: [''],
      expectedPaymentMonth: [''],
      confidence: [''],
      eventCompletionDate: [''],
      eventTypeCompleted: [''],
      eventTargetProduct: [''],
      partnerStatementSentDate: [''],
      statementNumber: [''],
      msInvoiceNumber: [''],
      msInvoicePaidDate: [''],
      paymentCentralTransactionId: [''],
      partnerPaymentProfileStatus: [''],
      supplierId: [''],
      payeeId: [''],
      payeeName: [''],
      agreement: [''],
      submittedDate: [''],
      milestoneOneCompletionDate: ['']
    });
    this.eventPoeInformationForm.disable();
  }

  loadEventPaymentInformation(): void {
    const dp = new DatePipe('en-US');
    const format = 'MM/dd/yyyy';
    this.eventDashboardService
      .getEventPaymentInformation(this.eventId, this.programTypeGUID)
      .subscribe(eventPaymentInformation => {
        if (eventPaymentInformation !== null) {
          this.eventPoeInformationForm.patchValue({
            scheduledEventDate: dp.transform(
              eventPaymentInformation.scheduledEventDate,
              format
            ),
            expectedPOEDate: dp.transform(
              eventPaymentInformation.expectedPOEDate,
              format
            ),
            expectedPaymentMonth: {
              fiscalMonthId: eventPaymentInformation.expectedPaymentMonthId,
              fiscal_Month: eventPaymentInformation.expectedPaymentMonth
            },
            confidence: {
              confidence: eventPaymentInformation.confidence,
              confidenceID: eventPaymentInformation.confidenceId
            },
            eventCompletionDate: dp.transform(
              eventPaymentInformation.eventCompletionDate,
              format
            ),
            eventTypeCompleted: {
              eventTypeId: eventPaymentInformation.eventTypeCompletedId,
              eventName: eventPaymentInformation.eventTypeCompleted
            },
            eventTargetProduct: {
              eventTargetProductId:
                eventPaymentInformation.eventTargetProductId,
              name: eventPaymentInformation.eventTargetProductName
            },
            partnerStatementSentDate: dp.transform(
              eventPaymentInformation.partnerStatementSentDate,
              format
            ),
            statementNumber: eventPaymentInformation.statementNumber,
            msInvoiceNumber: eventPaymentInformation.msInvoiceNumber,
            msInvoicePaidDate: dp.transform(
              eventPaymentInformation.msInvoicePaidDate,
              format
            ),
            paymentCentralTransactionId:
              eventPaymentInformation.paymentCentralTransactionId,
            supplierId: eventPaymentInformation.supplierId,
            payeeId: eventPaymentInformation.payeeId,
            payeeName: eventPaymentInformation.payeeName,
            submittedDate: dp.transform(
              eventPaymentInformation.submittedDate,
              format
            ),
            milestoneOneCompletionDate: dp.transform(
              eventPaymentInformation.milestoneOneCompletionDate,
              format
            )
          });

          this.isMultiTargetProductSupported =
            eventPaymentInformation.isMultiTargetProductSupported;
          if (eventPaymentInformation.partnerPaymentProfileStatus === true) {
            this.eventPoeInformationForm.controls[
              'partnerPaymentProfileStatus'
            ].setValue('true');
          } else {
            this.eventPoeInformationForm.controls[
              'partnerPaymentProfileStatus'
            ].setValue('false');
          }
          if (eventPaymentInformation.agreement === true) {
            this.eventPoeInformationForm.controls['agreement'].setValue('true');
          } else {
            this.eventPoeInformationForm.controls['agreement'].setValue(
              'false'
            );
          }
          this.isDealAssociation =
            eventPaymentInformation.eventTypeCompletedId == 9;
        } else {
          this.eventPoeInformationForm.reset();
        }
      });
  }

  editPoeInformation() {
    this.isReadonlyPOE = false;
    this.btnNavPoe = true;
    this.eventPoeInformationForm.enable();
  }

  CancelPoeInformation() {
    this.isReadonlyPOE = true;
    this.msgsPoe = [];
    this.btnNavPoe = false;
    this.loadEventPaymentInformation();
    this.eventPoeInformationForm.disable();
  }

  onSubmitPOEInformation() {
    this.buttonClicked = true;
    const dp = new DatePipe('en-US');
    const format = 'MM/dd/yyyy';

    const scheduledEventDate = dp.transform(
      this.eventPoeInformationForm.controls['scheduledEventDate'].value,
      format
    );

    const expectedPOEDate = dp.transform(
      this.eventPoeInformationForm.controls['expectedPOEDate'].value,
      format
    );

    const eventCompletionDate = dp.transform(
      this.eventPoeInformationForm.controls['eventCompletionDate'].value,
      format
    );

    const partnerStatementSentDate = dp.transform(
      this.eventPoeInformationForm.controls['partnerStatementSentDate'].value,
      format
    );

    const msInvoicePaidDate = dp.transform(
      this.eventPoeInformationForm.controls['msInvoicePaidDate'].value,
      format
    );

    const milestoneOneCompletionDate = dp.transform(
      this.eventPoeInformationForm.controls['milestoneOneCompletionDate'].value,
      format
    );

    const updateRequest: UpdateEventPaymentInformationRequest = {
      eventRegistrationId: this.eventId,
      scheduledEventDate:
        scheduledEventDate === null ? null : new Date(scheduledEventDate),
      expectedPOEDate:
        expectedPOEDate === null ? null : new Date(expectedPOEDate),
      expectedPaymentMonthId:
        this.eventPoeInformationForm.controls['expectedPaymentMonth'].value
          .fiscalMonthId,
      expectedPaymentMonth:
        this.eventPoeInformationForm.controls['expectedPaymentMonth'].value
          .fiscal_Month,
      confidenceId:
        this.eventPoeInformationForm.controls['confidence'].value.confidenceID,
      confidence:
        this.eventPoeInformationForm.controls['confidence'].value.confidence,
      eventCompletionDate:
        eventCompletionDate === null
          ? null
          : new Date(eventCompletionDate),
      eventTypeCompletedId:
        this.eventPoeInformationForm.controls['eventTypeCompleted'].value
          .eventTypeId,
      eventTargetProductId:
        this.eventPoeInformationForm.controls['eventTargetProduct'].value
          .eventTargetProductId,
      partnerStatementSentDate:
        partnerStatementSentDate === null
          ? null
          : new Date(partnerStatementSentDate),
      statementNumber:
        this.eventPoeInformationForm.controls['statementNumber'].value,
      msInvoiceNumber:
        this.eventPoeInformationForm.controls['msInvoiceNumber'].value,
      msInvoicePaidDate:
        msInvoicePaidDate === null ? null : new Date(msInvoicePaidDate),
      paymentCentralTransactionId:
        this.eventPoeInformationForm.controls['paymentCentralTransactionId']
          .value,
      partnerPaymentProfileStatus:
        this.eventPoeInformationForm.controls['partnerPaymentProfileStatus']
          .value === 'true'
          ? true
          : false,
      supplierId: this.eventPoeInformationForm.controls['supplierId'].value,
      milestoneOneCompletionDate:
        milestoneOneCompletionDate === null
          ? null
          : new Date(milestoneOneCompletionDate),
      programTypesGUID: this.isMRPPProgram
        ? ProgramTypesGUID.MRPP
        : ProgramTypesGUID.Events
    };

    this.eventDashboardService
      .updateEventPaymentInformation(updateRequest)
      .subscribe(
        res => {
          if (res) {
            this.eventPoeInformationForm.disable();
            this.btnNavPoe = !this.btnNavPoe;
            this.isReadonlyPOE = !this.isReadonlyPOE;
            this.loadEventPaymentInformation();
            this.msgsPoe = [];
            this.msgsPoe.push({
              severity: 'success',
              detail: 'Payment Information Updated Successfully'
            });
            this.buttonClicked = false;
          } else {
            this.msgsPoe = [];
            this.msgsPoe.push({
              severity: 'error',
              detail: 'Failed to Update Payment Information'
            });
          }
        },
        () => {
          this.msgsPoe = [];
          this.msgsPoe.push({
            severity: 'error',
            detail: 'Error occurred during updating Payment Information'
          });
        }
      );
  }

  getExpectedPaymentMonth() {
    this.osmEdit.getExpectedPaymentMonthDropDown().subscribe(PTList => {
      this.monthList = PTList;
    });
  }

  getConfidence() {
    this.osmEdit.getConfidenceDropDown().subscribe(PTList => {
      this.confidence = PTList;
    });
  }

  get EventWorkshop(): typeof EventWorkshop {
    return EventWorkshop;
  }

  getEventTypes() {
    const programTypeId = this.isMRPPProgram
      ? ProgramTypes.MRPP
      : ProgramTypes.Events;

    this.eventTypeService.getEventTypes(programTypeId).subscribe(PTList => {
      this.eventTypes = PTList.map(event => {
        return {
          eventName: event.eventName,
          eventTypeId: event.eventTypeId
        } as any;
      });
    });
  }

  getTargetProducts() {
    this.partnerWorkshopService
      .getEventTargetProductDropDown()
      .subscribe(PTList => {
        this.eventTargetProducts = PTList.map(eventtargetproduct => {
          return {
            eventTargetProductId: eventtargetproduct.eventTargetProductId,
            name: eventtargetproduct.name
          };
        });
      });
  }
}
